import _styled from "styled-components";
import React from "react";
import { type AutoPlayStatus } from "~/features/blocks/inventory/deep-dive-gallery/use-autoplay";
import { useVideoPreload, type Status } from "~/features/ui/video/use-video-preload";
import type { HlsVideoSource } from "~/features/ui/video/use-hls-video";
const AUTOPLAY_TIMEOUT = 5000;
export type Segment = {
  start: number;
  end: number;
};
export type DeepDiveGalleryControl = {
  videoStart?: number;
  videoEnd?: number;
};
type ContextProps = {
  hasStepsOverlay: boolean;
  videoSource: HlsVideoSource | undefined;
  videoPreloadStatus: Status;
  playVideoSegment: (segment: Segment) => void;
  isVideoPlaying: boolean;
  sequenceStarted: boolean;
  setSequenceStarted: React.Dispatch<React.SetStateAction<boolean>>;
  autoplayTimeout: number;
  setAutoplayTimeout: React.Dispatch<React.SetStateAction<number>>;
  autoPlayStatus: AutoPlayStatus;
  setAutoPlayStatus: React.Dispatch<React.SetStateAction<AutoPlayStatus>>;
  activeStepIndex: number;
  setActiveStepIndex: React.Dispatch<React.SetStateAction<number>>;
  controls: DeepDiveGalleryControl[];
  goNext: (autoPlayStatus?: AutoPlayStatus) => void;
  goPrev: (autoPlayStatus?: AutoPlayStatus) => void;
  goTo: (index: number, autoPlayStatus?: AutoPlayStatus) => void;
  renderGalleryCursor: boolean;
  setRenderGalleryCursor: React.Dispatch<React.SetStateAction<boolean>>;
};
const Context = React.createContext<ContextProps | undefined>(undefined);
type ProviderProps = Pick<ContextProps, "videoSource" | "hasStepsOverlay" | "controls"> & {
  children: React.ReactNode;
};
export const DeepDiveGalleryProvider = (props: ProviderProps) => {
  const {
    children,
    videoSource,
    hasStepsOverlay,
    controls
  } = props;
  const [sequenceStarted, setSequenceStarted] = React.useState(false);
  const [autoplayTimeout, setAutoplayTimeout] = React.useState(AUTOPLAY_TIMEOUT);
  const [activeStepIndex, setActiveStepIndex] = React.useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = React.useState(false);
  const [autoPlayStatus, setAutoPlayStatus] = React.useState<AutoPlayStatus>("paused");
  const [renderGalleryCursor, setRenderGalleryCursor] = React.useState(true);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const videoPreloadStatus = useVideoPreload({
    source: videoSource,
    videoRef
  });
  const activeSegmentRef = React.useRef<{
    element: HTMLVideoElement;
    onTimeUpdate: () => void;
  } | null>(null);
  const pendingSegmentRef = React.useRef<Segment | null>(null);
  const clearActiveSegment = React.useCallback(() => {
    const active = activeSegmentRef.current;
    if (!active) return;
    active.element.removeEventListener("timeupdate", active.onTimeUpdate);
    activeSegmentRef.current = null;
  }, []);
  const startVideoSegment = React.useCallback((segment: Segment) => {
    const videoElement = videoRef.current;
    if (!videoElement || videoPreloadStatus !== "PRELOADED" || !Number.isFinite(segment.start) || !Number.isFinite(segment.end) || segment.end <= segment.start) {
      return;
    }
    clearActiveSegment();
    videoElement.currentTime = segment.start;
    const onTimeUpdate = () => {
      if (videoElement.currentTime >= segment.end) {
        videoElement.pause();
        setIsVideoPlaying(false);
        clearActiveSegment();
      }
    };
    videoElement.addEventListener("timeupdate", onTimeUpdate);
    activeSegmentRef.current = {
      element: videoElement,
      onTimeUpdate
    };
    void videoElement.play().then(() => setIsVideoPlaying(true)).catch(() => {
      clearActiveSegment();
      setIsVideoPlaying(false);
    });
  }, [clearActiveSegment, videoPreloadStatus]);
  const playVideoSegment = React.useCallback((segment: Segment) => {
    pendingSegmentRef.current = segment;
    if (videoPreloadStatus !== "PRELOADED") {
      clearActiveSegment();
      setIsVideoPlaying(false);
      return;
    }
    pendingSegmentRef.current = null;
    startVideoSegment(segment);
  }, [clearActiveSegment, startVideoSegment, videoPreloadStatus]);
  React.useEffect(() => {
    if (videoPreloadStatus !== "PRELOADED" || !pendingSegmentRef.current) {
      return;
    }
    const segment = pendingSegmentRef.current;
    pendingSegmentRef.current = null;
    startVideoSegment(segment);
  }, [startVideoSegment, videoPreloadStatus]);
  React.useEffect(() => () => {
    clearActiveSegment();
    pendingSegmentRef.current = null;
  }, [clearActiveSegment, videoSource?.src]);
  const updateVideoAndIndex = (index: number, autoPlayStatus = "paused") => {
    const control = controls[index] || {};
    const controlSegment = {
      start: "videoStart" in control ? control.videoStart : 0,
      end: "videoEnd" in control ? control.videoEnd : 0
    };
    playVideoSegment(controlSegment as Segment);
    setAutoPlayStatus(autoPlayStatus as AutoPlayStatus);
    setActiveStepIndex(index);
  };
  const goPrev = () => {
    if (activeStepIndex === 0) return;
    updateVideoAndIndex(activeStepIndex - 1);
  };
  const goNext = () => {
    if (activeStepIndex === controls.length - 1) return;
    updateVideoAndIndex(activeStepIndex + 1);
  };
  const goTo = (index: number, autoPlayStatus = "paused") => {
    if (index < 0 || index >= controls.length) return;
    updateVideoAndIndex(index, autoPlayStatus);
  };
  const value = {
    videoSource,
    sequenceStarted,
    setSequenceStarted,
    hasStepsOverlay,
    autoplayTimeout,
    setAutoplayTimeout,
    activeStepIndex,
    setActiveStepIndex,
    videoPreloadStatus,
    playVideoSegment,
    isVideoPlaying,
    autoPlayStatus,
    setAutoPlayStatus,
    controls,
    goPrev,
    goNext,
    goTo,
    renderGalleryCursor,
    setRenderGalleryCursor
  };
  return <Context.Provider value={value}>
      {videoSource?.src && <_StyledVideo ref={videoRef} style={{
      opacity: videoPreloadStatus === "PRELOADED" ? 1 : 0
    }} playsInline muted onContextMenu={e => e.preventDefault()} />}
      {children}
    </Context.Provider>;
};
export const useDeepDiveGallery = (): ContextProps => {
  const context = React.useContext(Context);
  if (context === undefined) {
    throw new Error("useDeepDiveGallery must be used within a DeepDiveGalleryProvider");
  }
  return context;
};
var _StyledVideo = /*#__PURE__*/_styled("video").withConfig({
  displayName: "context___StyledVideo",
  componentId: "sc-r6b5z0-0"
})({
  "pointerEvents": "none",
  "position": "absolute",
  "inset": "0",
  "height": "100%",
  "width": "100%",
  "objectFit": "cover"
});